import letterSpacingFor from 'utils/letterSpacingFor'
import lineHeightFor from 'utils/lineHeightFor'

const AlignmentMap = {
  top: 'start',
  center: 'unsafe center',
  bottom: 'unsafe end',
}

export const verticalAlignmentToCss = (alignment) => {
  return AlignmentMap[alignment] || 'start'
}

export const richTextStyle = (element) => {
  const { height, textRotate, width } = element

  const alignContent = verticalAlignmentToCss(element.textVerticalAlign)

  if (textRotate === 90 || textRotate == 270) {
    return {
      alignContent,
      height: width,
      transform: [
        `translate(${(width - height) / 2}px, ${(height - width) / 2}px)`,
        `rotate(${textRotate}deg)`,
      ].join(' '),
      width: height,
    }
  }

  return {
    alignContent,
    height,
    transform: `translate(0px, 0px) rotate(${textRotate}deg)`,
    width,
  }
}

export const themeStyle = (theme, type, property) => {
  return theme.styles[type]?.[property] || theme.styles.paragraph[property]
}

const colorForType = (type, element) => {
  const colorName = `${type}Color`
  return element[colorName] || element.color
}

export const richTextElementStyle = (type, element, theme) => {
  if (type === 'link') {
    return {
      color: element.anchorColor,
    }
  }

  const baseFontSize = element.fontSize

  const fontSize = baseFontSize * themeStyle(theme, type, 'sizeScale')
  const letterSpacing = letterSpacingFor(fontSize)
  const lineHeight = lineHeightFor(
    themeStyle(theme, type, 'lineHeights'),
    fontSize
  )

  const color = colorForType(type, element)
  const textTransform =
    element.textCapitalisation == 'lower' ? 'lowercase' : 'none'

  return {
    color: color,
    fontSize: fontSize,
    fontFamily: themeStyle(theme, type, 'fontFamily'),
    fontWeight: themeStyle(theme, type, 'fontWeight'),
    lineHeight: `${lineHeight}px`,
    letterSpacing: `${letterSpacing}em`,
    textTransform: textTransform,
    marginBottom: Math.round(
      themeStyle(theme, type, 'marginBottom') * fontSize
    ),
  }
}

const TagNames = ['P', 'LI', 'A', 'H2', 'H3', 'H4']

const generateCommandsFromDom = (root, element, theme) => {
  const commands = []
  const styleStack = []
  let drawBullet = false
  let linkHref = null

  const walk = (node) => {
    if (!node) return

    const isTag = TagNames.includes(node.tagName)
    const style = styleStack.at(-1) || {}

    if (isTag) {
      const fontFamily = themeStyle(theme, node.dataset.type, 'fontFamily')
      const color = colorForType(node.dataset.type, element)

      styleStack.push({
        color,
        fontSize: parseFloat(node.style.fontSize) || style.fontSize,
        fontFamily,
        fontWeight: node.style.fontWeight || style.fontWeight,
        letterSpacing: node.style.letterSpacing || style.letterSpacing,
        textTransform: node.style.textTransform || style.textTransform,
      })
    }

    if (node.tagName === 'LI') {
      drawBullet = true
    } else if (node.tagName === 'A') {
      linkHref = node.href
    } else if (node.nodeType === Node.TEXT_NODE) {
      const textLines = extractLinesFromTextNode(node)

      for (const line of textLines) {
        commands.push({ ...line, ...style, drawBullet, linkHref })
        drawBullet = false
      }
    }

    // Recurse into children
    let nextNode = node.firstChild
    while (nextNode) {
      walk(nextNode)
      nextNode = nextNode.nextSibling
    }

    if (isTag) {
      styleStack.pop()
      drawBullet = false
      linkHref = null
    }
  }

  walk(root)

  return commands
}

export const extractRenderCommands = (editableNode, element, theme) => {
  const range = document.createRange()
  range.selectNodeContents(editableNode)

  const inverseCssTransform = inverseCssTransformMatrix(editableNode)

  const commands = generateCommandsFromDom(
    range.commonAncestorContainer,
    element,
    theme
  )

  // drawRectBoxes(commands)

  const offset = untransformRect(
    range.getBoundingClientRect(),
    inverseCssTransform
  )

  return commands.map((command) => {
    const rect = untransformRect(command, inverseCssTransform)

    return {
      ...command,
      x: rect.x - offset.x,
      y: rect.y - offset.y,
      width: rect.width,
      height: rect.height,
    }
  })
}

const toRect = (node) => {
  const domRect = node.getBoundingClientRect()

  // Add some padding due to rendered glyphs sometimes extending beyond the
  // clientRect.
  const lineHeight = domRect.height
  const padding = Math.max(Math.ceil(lineHeight * 0.1), 1)

  return {
    x: domRect.x,
    y: domRect.y,
    width: domRect.width + padding,
    height: domRect.height + padding,
  }
}

const extractLinesFromTextNode = (textNode) => {
  // A Range represents a fragment of the document which contains nodes and
  // parts of text nodes. One thing that's really cool about a Range is that we
  // can access the bounding boxes that contain the contents of the Range. By
  // incrementally adding characters - from our text node - into the range, and
  // then looking at the Range's client rectangles, we can determine which
  // characters belong in which rendered line.
  const textContent = textNode.textContent
  const range = document.createRange()
  let lines = []
  let line

  range.setStart(textNode, 0)

  // Iterate over every character in the text node.
  for (let i = 0; i < textContent.length; i++) {
    range.setEnd(textNode, i + 1)

    // At this point, the Range's client rectangles will include a rectangle
    // for each visually-rendered line of text. Which means, the last
    // character in our Range (the current character in our for-loop) will be
    // the last character in the last line of text (in our Range). As such, we
    // can use the current rectangle count to determine the line of text.
    const lineIndex = range.getClientRects().length - 1

    // If this is the first character in this line, create a new buffer for
    // this line.
    if (!lines[lineIndex]) {
      line = { startIndex: i, endIndex: i + 1, chars: [] }
      lines.push(line)
    }

    // Add this character to the currently pending line of text.
    line.chars.push(textContent.charAt(i))
    line.endIndex = i + 1
  }

  // At this point, we have an array (lines) of arrays (characters). Let's
  // collapse the character buffers down into a single text value.
  return lines.reduce((acc, line) => {
    if (line.endIndex - line.startIndex <= 1) return acc

    range.setStart(textNode, line.startIndex)
    range.setEnd(textNode, line.endIndex)

    const rect = toRect(range)

    acc.push({
      text: line.chars.join(''),
      ...rect,
    })

    return acc
  }, [])
}

const untransformRect = (rect, inverseMatrix) => {
  const points = [
    new DOMPoint(rect.x, rect.y),
    new DOMPoint(rect.x + rect.width, rect.y),
    new DOMPoint(rect.x + rect.width, rect.y + rect.height),
    new DOMPoint(rect.x, rect.y + rect.height),
  ]

  const untransformedPoints = points.map((p) =>
    p.matrixTransform(inverseMatrix)
  )

  const xs = untransformedPoints.map((p) => p.x)
  const ys = untransformedPoints.map((p) => p.y)

  const left = Math.min(...xs)
  const top = Math.min(...ys)
  const right = Math.max(...xs)
  const bottom = Math.max(...ys)

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  }
}

const inverseCssTransformMatrix = (el) => {
  let matrix = new DOMMatrix()
  while (el && el !== document.body) {
    const transform = getComputedStyle(el).transform
    if (transform && transform !== 'none') {
      matrix = new DOMMatrix(transform).multiply(matrix)
    }
    el = el.parentElement
  }
  return matrix.inverse()
}

// For debugging
// eslint-disable-next-line no-unused-vars -- used when debugging in dev tools
const drawRectBoxes = (clientRects) => {
  Array.from(document.querySelectorAll('.box')).forEach((node) => node.remove())

  const colors = ['red', 'green', 'blue']

  Array.from(clientRects).forEach((rect, i) => {
    const color = colors[i % colors.length]

    const box = document.createElement('div')
    box.classList.add('box')
    box.style.position = 'absolute'
    box.style.border = `1px solid ${color}`
    box.style.top = `${rect.y}px`
    box.style.left = `${rect.x}px`
    box.style.width = `${rect.width}px`
    box.style.height = `${rect.height}px`
    box.style.pointerEvents = 'none'
    document.body.appendChild(box)
  })
}
